import Swiper from "swiper";
import { Navigation } from "swiper/modules";

const MOBILE_BREAKPOINT = 768;
const LOOP_REPEAT_COUNT = 3;
const LOOP_MIDDLE_REPEAT_INDEX = 1;

function normalizeBoolean(value) {
  return value === true || value === 1 || value === "1" || value === "true";
}

export default {
  data: () => ({
    swiperInstance: null,
    _prevRealIndex: 0,
    _pendingRealIndex: 0,
    _pendingOfferKey: null,
    _hasInitializedMobileSwiper: false,
    _isNormalizingSwiperPosition: false,
    hasMovedFromFirst: false,
    isMobile: false,
  }),

  computed: {
    cardsSwiperFlag() {
      const settings = this.settings || {};
      const value = settings.isSwiperEnabled ?? settings.cards_swiper_paidbroker;

      return normalizeBoolean(value);
    },

    mobileSwiperOffers() {
      if (Array.isArray(this.sortedOffers)) return this.sortedOffers;
      if (Array.isArray(this.showcaseOffers)) return this.showcaseOffers;
      if (Array.isArray(this.filteredOffers)) return this.filteredOffers;

      return [];
    },

    mobileSwiperDisplayOffers() {
      const offers = this.mobileSwiperOffers;

      if (!this.isSwiperEnabled || offers.length <= 1) {
        return offers.map((offer, index) => this.buildMobileSwiperOffer(offer, index, 0));
      }

      return Array.from({ length: LOOP_REPEAT_COUNT }).flatMap((_, repeatIndex) => (
        offers.map((offer, index) => this.buildMobileSwiperOffer(offer, index, repeatIndex))
      ));
    },

    isSwiperEnabled() {
      return this.isMobile && this.cardsSwiperFlag && this.mobileSwiperOffers.length > 0;
    },
  },

  watch: {
    isSwiperEnabled(enabled) {
      if (!enabled) {
        this.destroySwiper();
        return;
      }

      this.$nextTick(() => this.initMobileSwiper());
    },

    mobileSwiperOffers(_nextOffers, previousOffers) {
      if (!this.isSwiperEnabled) return;

      this.capturePendingSwiperState(this.swiperInstance, previousOffers);
      this.$nextTick(() => this.initMobileSwiper());
    },
  },

  mounted() {
    this.detectMobile();
    window.addEventListener("resize", this.handleResize);

    this.$nextTick(() => {
      if (this.isSwiperEnabled) this.initMobileSwiper();
    });
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
    this.destroySwiper();
  },

  methods: {
    detectMobile() {
      this.isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    },

    handleResize() {
      const wasMobile = this.isMobile;
      this.detectMobile();

      if (wasMobile !== this.isMobile) return;

      if (this.isSwiperEnabled && this.swiperInstance) this.swiperInstance.update();
    },

    destroySwiper() {
      if (!this.swiperInstance) return;

      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
      this.renderMobileSwiperPagination(0);
    },

    initMobileSwiper() {
      if (!this.$refs.swiper) return;

      const offers = this.mobileSwiperOffers;
      const total = offers.length;
      const initialRealIndex = this._hasInitializedMobileSwiper
        ? this.resolvePendingSwiperRealIndex(offers)
        : 0;
      const initialSlide = this.getMobileSwiperSlideIndex(initialRealIndex);

      this.destroySwiper();

      this.swiperInstance = new Swiper(this.$refs.swiper, {
        modules: [Navigation],
        slidesPerView: "auto",
        centeredSlides: true,
        spaceBetween: 20,
        initialSlide,
        loop: false,
        observer: true,
        observeParents: true,
        navigation: {
          prevEl: this.$refs.prevBtn,
          nextEl: this.$refs.nextBtn,
        },
        on: {
          init: (swiper) => {
            const realIndex = this.getMobileSwiperRealIndex(swiper.activeIndex);

            this.rememberActiveSwiperOffer(swiper, offers);
            this._prevRealIndex = realIndex;
            this.hasMovedFromFirst = total > 1;
            this._hasInitializedMobileSwiper = true;
            this.renderMobileSwiperPagination(realIndex);
          },

          realIndexChange: (swiper) => {
            const currentIndex = this.getMobileSwiperRealIndex(swiper.activeIndex);

            this._prevRealIndex = currentIndex;
            this.rememberActiveSwiperOffer(swiper, this.mobileSwiperOffers);
            this.renderMobileSwiperPagination(currentIndex);

            this.hasMovedFromFirst = total > 1;
          },

          slideChange: (swiper) => {
            const currentIndex = this.getMobileSwiperRealIndex(swiper.activeIndex);

            this._prevRealIndex = currentIndex;
            this.rememberActiveSwiperOffer(swiper, this.mobileSwiperOffers);
            this.renderMobileSwiperPagination(currentIndex);

            this.hasMovedFromFirst = total > 1;
          },

          slideChangeTransitionEnd: (swiper) => {
            this.normalizeMobileSwiperPosition(swiper);
          },
        },
      });
    },

    capturePendingSwiperState(swiper = this.swiperInstance, offers = this.mobileSwiperOffers) {
      const realIndex = Number.isFinite(swiper?.activeIndex)
        ? this.getMobileSwiperRealIndex(swiper.activeIndex)
        : 0;
      const activeOffer = Array.isArray(offers) ? offers[realIndex] : null;

      this._pendingRealIndex = realIndex;
      this._pendingOfferKey = this.getOfferKey?.(activeOffer) || null;
    },

    resolvePendingSwiperRealIndex(offers = this.mobileSwiperOffers) {
      const list = Array.isArray(offers) ? offers : [];

      if (list.length === 0) return 0;

      if (this._pendingOfferKey && this.getOfferKey) {
        const matchedIndex = list.findIndex((offer) => this.getOfferKey(offer) === this._pendingOfferKey);

        if (matchedIndex >= 0) return matchedIndex;
      }

      const fallbackIndex = Number.isFinite(this._pendingRealIndex) ? this._pendingRealIndex : 0;

      return Math.min(Math.max(fallbackIndex, 0), list.length - 1);
    },

    rememberActiveSwiperOffer(swiper = this.swiperInstance, offers = this.mobileSwiperOffers) {
      this.capturePendingSwiperState(swiper, offers);
    },

    buildMobileSwiperOffer(offer, index, repeatIndex) {
      return {
        ...offer,
        __mobileSwiperRealIndex: index,
        __mobileSwiperLoopKey: `${repeatIndex}-${this.getOfferKey?.(offer) || index}`,
      };
    },

    getMobileSwiperRealIndex(slideIndex = 0) {
      const total = this.mobileSwiperOffers.length;

      if (total <= 0) return 0;

      return ((slideIndex % total) + total) % total;
    },

    getMobileSwiperSlideIndex(realIndex = 0) {
      const total = this.mobileSwiperOffers.length;

      if (total <= 1) return 0;

      return total * LOOP_MIDDLE_REPEAT_INDEX + realIndex;
    },

    normalizeMobileSwiperPosition(swiper = this.swiperInstance) {
      const total = this.mobileSwiperOffers.length;

      if (!swiper || total <= 1 || this._isNormalizingSwiperPosition) return;

      const activeIndex = swiper.activeIndex;
      const minMiddleIndex = total * LOOP_MIDDLE_REPEAT_INDEX;
      const maxMiddleIndex = minMiddleIndex + total - 1;

      if (activeIndex >= minMiddleIndex && activeIndex <= maxMiddleIndex) return;

      this._isNormalizingSwiperPosition = true;
      swiper.slideTo(this.getMobileSwiperSlideIndex(this.getMobileSwiperRealIndex(activeIndex)), 0, false);
      this._isNormalizingSwiperPosition = false;
    },

    renderMobileSwiperPagination(activeIndex = 0) {
      const pagination = this.$refs.pagination;
      const total = this.mobileSwiperOffers.length;

      if (!pagination) return;

      pagination.innerHTML = "";

      if (total <= 1) return;

      for (let index = 0; index < total; index += 1) {
        const bullet = document.createElement("button");
        bullet.type = "button";
        bullet.className = "showcase-pagination-bullet";

        if (index === activeIndex) {
          bullet.classList.add("showcase-pagination-bullet-active");
        }

        bullet.addEventListener("click", () => {
          this.swiperInstance?.slideTo(this.getMobileSwiperSlideIndex(index));
        });

        pagination.appendChild(bullet);
      }
    },

  },
};
